import { FC } from "react";
import { IItemGroup, VerdictKind } from "../../ScannerApp";
import { countPieces, countProducts, statusAr, t } from "../../i18n/ar";
import { IOrderResponse } from "../../interfaces/responses/OrderResponse.interface";
import { IStoreResponseData } from "../../interfaces/responses/StoreResponseData.interface";
import StoreScoreChip from "../../../common/StoreScoreChip";

interface IVerdictProps {
    kind: VerdictKind;
    error: string;
    lastCode: string;
    order: IOrderResponse | null;
    store: IStoreResponseData | null;
    groups: IItemGroup[];
    totalUnits: number;
    rtsUnits: number;
}

// The ONE thing the worker must understand in under a second:
//   green  ✅  → pack and send
//   red    ⛔  → do NOT send
//   orange ⚠️  → send only the green cards below
// Everything is color + icon + a giant number; text is secondary.
export const Verdict: FC<IVerdictProps> = ({ kind, error, lastCode, order, store, groups, totalUnits, rtsUnits }) => {
    if (kind === "idle") {
        return (
            <div className="mt-6 flex flex-col items-center justify-center rounded-3xl border-4 border-dashed border-slate-300 bg-white/60 px-4 py-14 text-center">
                <div className="scan-pulse text-[7rem] leading-none sm:text-[9rem]" aria-hidden>📦</div>
                <p className="mt-4 text-4xl font-black text-slate-700 sm:text-5xl">{t.scanPrompt}</p>
                <p className="mt-2 text-xl font-bold text-slate-400">{t.scanSub}</p>
            </div>
        );
    }

    if (kind === "notfound") {
        return (
            <div className="shake mt-4 rounded-3xl border-4 border-red-700 bg-red-600 px-4 py-8 text-center text-white shadow-xl">
                <div className="text-[6rem] leading-none sm:text-[8rem]" aria-hidden>❌</div>
                <p className="mt-2 text-4xl font-black sm:text-5xl">{error || t.notFoundTitle}</p>
                <p className="mt-2 text-2xl font-bold text-red-100">🔄 {t.notFoundSub}</p>
                {lastCode && (
                    <p className="ltr mt-3 inline-block rounded-lg bg-red-800/60 px-4 py-1 font-mono text-xl tracking-widest">
                        {lastCode}
                    </p>
                )}
            </div>
        );
    }

    const distinctProducts = groups.length;
    const nonRtsStatuses = Array.from(
        new Set((order?.items || []).filter((i) => i.status !== "READY_TO_SHIP").map((i) => statusAr(String(i.status))))
    );

    const theme = {
        go: {
            wrap: "border-emerald-800 bg-emerald-600 text-white",
            icon: "✅",
            title: t.goTitle,
            sub: t.goSub,
            extra: "",
        },
        stop: {
            wrap: "border-red-800 bg-red-600 text-white flash-red",
            icon: "⛔",
            title: t.stopTitle,
            sub: nonRtsStatuses.join(" / ") || t.stopSub,
            extra: "",
        },
        caution: {
            wrap: "border-amber-600 bg-amber-400 text-slate-900 flash-amber",
            icon: "⚠️",
            title: t.cautionTitle,
            sub: `✅ ${countPieces(rtsUnits)} من ${totalUnits}`,
            extra: "",
        },
    }[kind];

    return (
        <div className={`mt-4 rounded-3xl border-4 px-4 py-6 text-center shadow-xl ${theme.wrap}`}>
            <div className="flex flex-col items-center justify-center gap-2 sm:flex-row sm:gap-6">
                <div className="text-[5.5rem] leading-none sm:text-[7rem]" aria-hidden>{theme.icon}</div>
                <div>
                    <p className="text-4xl font-black leading-tight sm:text-6xl">{theme.title}</p>
                    <p className="mt-1 text-xl font-bold opacity-90 sm:text-2xl">{theme.sub}</p>
                </div>
            </div>

            {/* Big counters: how many different products, how many pieces total */}
            <div className="mt-5 flex flex-wrap items-stretch justify-center gap-3">
                <Counter icon="🧾" big={distinctProducts} label={countProducts(distinctProducts)} warn={distinctProducts > 1} />
                <Counter icon="📦" big={totalUnits} label={countPieces(totalUnits)} warn={totalUnits > 1} />
            </div>

            {/* Order + store identity, small — for the supervisor, not the worker */}
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-base font-bold">
                {order?.number && (
                    <span className="ltr rounded-lg bg-black/20 px-3 py-1 font-mono tracking-wider">#{order.number}</span>
                )}
                {store && <span className="rounded-lg bg-black/20 px-3 py-1">🏬 {store.name}<StoreScoreChip store={store.name} size="md" /></span>}
            </div>
        </div>
    );
};

const Counter: FC<{ icon: string; big: number; label: string; warn: boolean }> = ({ icon, big, label, warn }) => (
    <div
        className={`flex min-w-[9rem] flex-col items-center rounded-2xl px-5 py-2 ${
            warn ? "bg-white text-slate-900 ring-4 ring-amber-500" : "bg-black/15"
        }`}
    >
        <div className="flex items-center gap-2">
            <span className="text-3xl" aria-hidden>{icon}</span>
            <span className="ltr text-6xl font-black leading-none">{big}</span>
        </div>
        <span className="mt-1 text-lg font-bold">{label}</span>
    </div>
);
